import React from 'react';
import styles from './Meetings.module.scss';
import useApiData from '../../../../../hooks/useApiData';
import Loading from '../../../Common/Loading/Loading';
import APIClient from '../../../../../util/APIClient';
import Error, { getErrorComponentFromHttpError } from '../../../Common/Error/Error';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import MeetingDetailsView from '../../../Common/MeetingDetailsView/MeetingDetailsView';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { getWeekBoundsForDate } from '../../../../../util/formatDate';

export default function Meetings() {
  const [selectedTeamId, setSelectedTeamId] = React.useState(null);
  const [selectedMeetingId, setSelectedMeetingId] = React.useState(null);
  const [selectedWeek, setSelectedWeek] = React.useState(getWeekBoundsForDate());
  const [teamMeetings, setTeamMeetings] = React.useState([]);
  const {
    data: teamsData,
    error: teamsError,
    isLoading: teamsIsLoading,
  } = useApiData({
    path: `/administrator/teams`,
    params: {
      includeTeamMemberCounts: true,
      includeGoalCounts: true,
    },
  });

  function incrementSelectedWeek() {
    setSelectedWeek((currentlySelectedWeek) => {
      const d = new Date(currentlySelectedWeek.maxTime).setUTCDate(currentlySelectedWeek.maxTime.getUTCDate() + 1);
      return getWeekBoundsForDate(d);
    });
  }
  function decrementSelectedWeek() {
    setSelectedWeek((currentlySelectedWeek) => {
      const d = new Date(currentlySelectedWeek.minTime).setUTCDate(currentlySelectedWeek.minTime.getUTCDate() - 1);
      return getWeekBoundsForDate(d);
    });
  }

  async function getTeamMeetings(teamId) {
    try {
      const meetingsData = await APIClient.get(`/administrator/teams/${teamId}`, {
        params: {
          includeMeetingSummaries: true,
        },
      });
      return meetingsData.data.meetings;
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  React.useEffect(() => {
    if (selectedTeamId) {
      getTeamMeetings(selectedTeamId).then((teamMeetings) => {
        setTeamMeetings(teamMeetings);
      });
    }
  }, [selectedTeamId]);

  React.useEffect(() => {
    const matchingMeetingId =
      teamMeetings?.filter(
        (teamMeeting) =>
          new Date(teamMeeting.scheduledTime) >= selectedWeek.minTime &&
          new Date(teamMeeting.scheduledTime) <= selectedWeek.maxTime,
      )?.[0]?.id || null;
    setSelectedMeetingId(matchingMeetingId);
  }, [teamMeetings, selectedWeek]);

  React.useEffect(() => {
    if (teamsData && teamsData.length > 0) {
      setSelectedTeamId(teamsData[0].id);
    } else {
      setSelectedTeamId('');
    }
  }, [teamsData]);

  if (teamsIsLoading) {
    return <Loading message="Fetching data ..." />;
  }

  if (teamsError || teamsData === null) {
    return getErrorComponentFromHttpError(teamsError);
  }

  return (
    <div className={styles.meetings}>
      <h1>Meetings</h1>
      <div className={styles.header}>
        <FormControl className={styles.teamSelector}>
          <InputLabel id="team-select-label">Team</InputLabel>
          <Select
            variant="outlined"
            labelId="team-select-label"
            id="team-select"
            label="Team"
            onChange={(e) => {
              setSelectedMeetingId(null);
              setSelectedTeamId(e.target.value);
            }}
            value={selectedTeamId}
            renderValue={(selectedTeamId) =>
              teamsData.filter((t) => t.id === selectedTeamId)?.[0]?.label || 'Select team'
            }
          >
            {teamsData.map((t) => (
              <MenuItem key={`team-${t.id}`} value={t.id}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div className={styles.weekSelector}>
          <Button variant="text" onClick={decrementSelectedWeek}>
            <ArrowBackIcon />
          </Button>
          <p>
            {selectedWeek.minTime
              .toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                timeZone: 'UTC',
                ...(selectedWeek.minTime.getUTCFullYear() === selectedWeek.maxTime.getUTCFullYear()
                  ? {}
                  : { year: 'numeric' }),
              })
              .replaceAll(/ /g, '\u00A0')}
            {'\u00A0to\u00A0'}
            {selectedWeek.maxTime
              .toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                timeZone: 'UTC',
              })
              .replaceAll(/ /g, '\u00A0')}
          </p>
          <Button variant="text" onClick={incrementSelectedWeek}>
            <ArrowForwardIcon />
          </Button>
        </div>
      </div>
      {(selectedTeamId && selectedMeetingId && (
        <MeetingDetailsView
          isAdministratorView={true}
          useSecondaryHeadingLevel={true}
          hideAttendance={true}
          teamId={selectedTeamId}
          meetingId={selectedMeetingId}
          key={`${selectedTeamId}_${selectedMeetingId}`}
        />
      )) || (
        <Error
          icon={<SearchRoundedIcon fontSize="inherit" />}
          title="Meeting not found"
          text="This team does not have a meeting that was scheduled or took place within the selected time window."
        />
      )}
    </div>
  );
}
